import { useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Spinner } from 'react-bootstrap'
import { BsCalendar3, BsChevronDown } from 'react-icons/bs'
import { useDebounce } from 'use-debounce'
import { format, differenceInDays } from 'date-fns'
import { CompanyPageAccordion } from '../CompanyPageAccordion'
import { Tabs } from '../../Tabs'
import { EventDateGroup } from './EventDateGroup'
import { TimelineConnector } from './TimelineConnector'
import { groupEventsByDate } from './utils'
import apiService from 'services/api'
import { Popover, PopoverContent, PopoverTrigger } from '../../UI/popover'
import { Calendar } from '../../UI/calendar'
import { SampleRequestPreviewModal } from '../../Samples/SampleRequestPreviewModal'

type EventHistoryTab =
  | 'all'
  | 'deals'
  | 'notes'
  | 'reminders'
  | 'samples'
  | 'account_attributes'

const EVENT_HISTORY_TABS = [
  { key: 'all' as const, title: 'All' },
  { key: 'deals' as const, title: 'Deals' },
  { key: 'notes' as const, title: 'Notes' },
  { key: 'reminders' as const, title: 'Reminders' },
  { key: 'samples' as const, title: 'Samples' },
  { key: 'account_attributes' as const, title: 'Account Attributes' },
]

interface EventHistoryAccordionProps {
  chainProxyId: number
  doorProxyId?: number
  companyType?: string
}

export function EventHistoryAccordion({
  chainProxyId,
  doorProxyId,
}: EventHistoryAccordionProps) {
  const api = apiService()
  const [selectedTab, setSelectedTab] = useState<EventHistoryTab>('all')
  const searchTerm = ''
  // const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)
  const [dateRange, setDateRange] = useState<
    | {
        from: Date
        to?: Date
      }
    | undefined
  >(undefined)

  // Sample preview modal state
  const [showPreviewModal, setShowPreviewModal] = useState(false)
  const [previewingSampleId, setPreviewingSampleId] = useState<number | null>(
    null
  )

  // Sample preview modal handlers
  const handlePreviewRequest = (sampleId: number) => {
    setPreviewingSampleId(sampleId)
    setShowPreviewModal(true)
  }

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false)
    setPreviewingSampleId(null)
  }

  const PAGE_SIZE = 20

  // Build query params for each page
  const buildQueryParams = (pageParam: number) => {
    const baseParams: Record<string, string | number> = {
      page_size: PAGE_SIZE,
      page: pageParam,
    }

    // Add door proxy filter if on door page
    if (doorProxyId) {
      baseParams.door_proxy_id = doorProxyId
    }

    // Add search parameter if search term exists
    if (debouncedSearchTerm.trim()) {
      baseParams.search = debouncedSearchTerm.trim()
    }

    // Add event_type filter based on selected tab
    if (selectedTab !== 'all') {
      const eventTypeMap: Record<EventHistoryTab, string> = {
        all: '', // not used
        deals: 'deal',
        notes: 'notes', // Backend maps this to companynote, contactnote, doornote
        reminders: 'reminder',
        samples: 'samples', // Backend maps this to samplerequest
        account_attributes: 'contactcompany',
      }
      baseParams.event_type = eventTypeMap[selectedTab]
    }

    // Add date range filters
    if (dateRange?.from) {
      baseParams.created_at_greater = dateRange.from.toISOString()
    }
    if (dateRange?.to) {
      baseParams.created_at_less = dateRange.to.toISOString()
    }

    return baseParams
  }

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: [
      'companyActivity',
      chainProxyId,
      doorProxyId,
      selectedTab,
      debouncedSearchTerm,
      dateRange,
    ],
    queryFn: ({ pageParam }) =>
      api.getCompanyActivityList(chainProxyId, buildQueryParams(pageParam)),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0
      )
      if (totalFetched < lastPage.count) {
        return allPages.length + 1
      }
      return undefined
    },
    enabled: !!chainProxyId,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  // Flatten all pages of events into a single array and group by date
  const groupedEvents = useMemo(() => {
    if (!data?.pages) return []
    const allEvents = data.pages.flatMap((page) => page.results)
    return groupEventsByDate(allEvents)
  }, [data?.pages])

  // Get the most recent event for the header
  const mostRecentEvent = useMemo(() => {
    if (!data?.pages?.[0]?.results?.[0]) return null
    return data.pages[0].results[0]
  }, [data?.pages])

  // Calculate days since last update
  const daysSinceUpdate = useMemo(() => {
    if (!mostRecentEvent?.created_at) return 0
    return differenceInDays(new Date(), new Date(mostRecentEvent.created_at))
  }, [mostRecentEvent])

  // Get color for the days badge based on thresholds
  const getDaysBadgeColor = (days: number) => {
    if (days < 30) return '#57D36F' // Green
    if (days < 90) return '#FAB661' // Orange
    return '#EA4056' // Red
  }

  // Header content with last updated info
  const headerRight = mostRecentEvent ? (
    <>
      {/* Last Updated Date */}
      <div className="flex items-center gap-1">
        <BsCalendar3 className="w-4 h-4 text-[#6E6E6E]" />
        <span
          className="text-[10px] font-light text-[#191D23]"
          style={{ lineHeight: '18px' }}
        >
          Updated {format(new Date(mostRecentEvent.created_at), 'MM/dd/yyyy')}
        </span>
      </div>

      {/* Days Badge */}
      <div className="flex h-5 items-center gap-1 rounded-full border border-[#E2E2E2] bg-[#FAFAFA] px-1.5 py-0.5">
        <div
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: getDaysBadgeColor(daysSinceUpdate) }}
        />
        <span
          className="text-[10px] font-light text-[#191D23]"
          style={{ lineHeight: '100%' }}
        >
          <span className="font-semibold">{daysSinceUpdate}</span>{' '}
          {daysSinceUpdate === 1 ? 'day' : 'days'}
        </span>
      </div>
    </>
  ) : null

  return (
    <CompanyPageAccordion
      title="Event History"
      defaultOpen={true}
      stickyHeader={true}
      headerRight={headerRight}
    >
      {/* Tabs and Search - sticky header */}
      <div
        className="sticky bg-gray-50 z-30 space-y-4 pb-4 relative before:absolute before:inset-x-0 before:top-0 before:-translate-y-full before:h-4 before:bg-gradient-to-t before:from-gray-50 before:to-transparent before:pointer-events-none"
        style={{ top: 'calc(var(--company-top-card-height, 0px) + 48px)' }}
      >
        {/* Tabs */}
        <Tabs
          tabs={EVENT_HISTORY_TABS}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          className="border-b border-gray-200"
        />

        {/* Search Bar */}
        {/* <div className="relative">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
            <BsSearch className="w-4 h-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search for an event"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-fb-dark-green focus:border-fb-dark-green outline-none"
          />
        </div> */}

        {/* Filters Row */}
        <div className="w-full">
          {/* Date Range Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <button className="w-full flex items-center gap-2 px-3.5 py-2 bg-white border border-[#E2E2E2] rounded-md text-sm text-[#6E6E6E] hover:border-fb-dark-green transition-colors">
                <BsCalendar3 className="w-5 h-5 flex-shrink-0" />
                <span className="flex-1 text-left">
                  {dateRange?.from ? (
                    dateRange.to ? (
                      <>
                        {format(dateRange.from, 'd MMM yyyy')} -{' '}
                        {format(dateRange.to, 'd MMM yyyy')}
                      </>
                    ) : (
                      format(dateRange.from, 'd MMM yyyy')
                    )
                  ) : (
                    'All time'
                  )}
                </span>
                <BsChevronDown className="w-5 h-5 flex-shrink-0" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={(newRange) => {
                  if (!newRange?.from) {
                    return
                  }
                  setDateRange(newRange as { from: Date; to?: Date })
                }}
                numberOfMonths={2}
              />
              {dateRange && (
                <div className="p-3 border-t">
                  <button
                    onClick={() => setDateRange(undefined)}
                    className="w-full px-3 py-1.5 text-sm text-gray-600 hover:text-fb-dark-green transition-colors"
                  >
                    Clear dates
                  </button>
                </div>
              )}
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <div className="relative min-h-[100px]">
        {/* Error state */}
        {error && (
          <div className="py-8 text-center text-sm text-red-500">
            Failed to load event history
          </div>
        )}

        {/* Empty state */}
        {!error && data && groupedEvents.length === 0 && !isLoading && (
          <div className="py-8 text-center text-sm text-gray-500">
            No recent events found
          </div>
        )}

        {/* Content */}
        {!error && groupedEvents.length > 0 && (
          <div className="mt-4 space-y-6 relative">
            {/* Timeline connector - simple CSS line behind icons */}
            <TimelineConnector />

            {/* Event groups */}
            {groupedEvents.map((group) => (
              <EventDateGroup
                key={group.date}
                group={group}
                onPreviewRequest={handlePreviewRequest}
              />
            ))}

            {/* Load More Button */}
            {hasNextPage && (
              <div className="flex justify-center pt-4 pb-8">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="px-6 py-2 bg-fb-dark-green text-white rounded-lg hover:bg-fb-dark-green-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isFetchingNextPage ? (
                    <>
                      <Spinner animation="border" size="sm" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <span>Load More Events</span>
                  )}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Loading overlay for tab/search switching (when we have previous data) */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50 rounded">
            <Spinner animation="border" size="sm" />
          </div>
        )}
      </div>

      {/* Sample Preview Modal */}
      {showPreviewModal && (
        <SampleRequestPreviewModal
          show={showPreviewModal}
          onHide={handleClosePreviewModal}
          sampleRequestId={previewingSampleId}
        />
      )}
    </CompanyPageAccordion>
  )
}
